import { isSameDay } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import PropTypes from 'prop-types'
import { findMatchBetweenPlayers, getHomeAwayStatus } from '../../utils/match'

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone

const WeeklyCalendarOverlays = ({
  weekProposals,
  weekScheduledMatches,
  weekDates,
  TIME_SLOTS,
  unplayedOpponents,
  allMatches,
  currentUserPlayerId,
  divisionRoster,
  handleProposalClick,
  handleMatchClick,
}) => (
  <div className="overlay-grid">
    {weekProposals.map((proposal) => {
      const startTime = toZonedTime(proposal.proposed_time, TZ)
      const weekDayIndex = weekDates.findIndex((d) => isSameDay(d, startTime))

      if (weekDayIndex === -1) return null

      const timeSlotIndex = TIME_SLOTS.findIndex(
        (slot) =>
          slot.hour === startTime.getHours() &&
          slot.minute === startTime.getMinutes(),
      )

      if (timeSlotIndex === -1) return null

      const DURATION_SLOTS = 3
      const opponentId = proposal.is_sent_by_user
        ? proposal.opponent_player_id
        : proposal.proposer_player_id
      let opponent = unplayedOpponents.find((o) => o.id === opponentId)

      if (!opponent && (proposal.opponent_name || proposal.proposer_name)) {
        const name = proposal.is_sent_by_user
          ? proposal.opponent_name
          : proposal.proposer_name
        const nameParts = name ? name.split(' ') : ['Unknown']
        opponent = {
          id: opponentId,
          first_name: nameParts[0] || '',
          last_name: nameParts.slice(1).join(' '),
        }
      }

      let slotContent = ''
      let title = ''
      let slotClass = 'proposal-card-overlay'

      const proposalMatch = findMatchBetweenPlayers(
        allMatches,
        currentUserPlayerId,
        opponentId,
      )
      const { isHome: isCurrentPlayerHome, icon: homeAwayIcon } =
        getHomeAwayStatus(proposalMatch, currentUserPlayerId)

      if (proposal.status === 'pending') {
        slotClass += ' pending'
        const opponentName = opponent
          ? `${opponent.first_name} ${opponent.last_name.charAt(0)}.`
          : proposal.is_sent_by_user
            ? proposal.opponent_name
            : proposal.proposer_name
        if (proposal.is_sent_by_user) {
          slotContent = `→ ${opponentName}`
          title = `Proposal sent to ${opponentName}`
        } else {
          slotContent = `← ${opponentName}`
          title = `Proposal from ${opponentName}`
        }
      } else if (proposal.status === 'declined') {
        slotClass += ' declined'
        const opponentName = opponent
          ? `${opponent.first_name} ${opponent.last_name.charAt(0)}.`
          : proposal.is_sent_by_user
            ? proposal.opponent_name
            : proposal.proposer_name
        slotContent = `✕ ${opponentName}`
        title = `Proposal rejected by ${opponentName} (click to dismiss)`
      } else {
        slotClass += ' responded'
        const opponentName = opponent
          ? `${opponent.first_name} ${opponent.last_name.charAt(0)}.`
          : proposal.is_sent_by_user
            ? proposal.opponent_name
            : proposal.proposer_name
        slotContent = `Proposal with ${opponentName}`
        title = `Proposal with ${opponentName} (${proposal.status})`
      }

      return (
        <div
          key={`prop-${proposal.id}`}
          className={slotClass}
          style={{
            gridColumn: weekDayIndex + 2,
            gridRow: `${timeSlotIndex + 2} / span ${DURATION_SLOTS}`,
          }}
          onClick={(e) => {
            e.stopPropagation()
            handleProposalClick(proposal, opponent)
          }}
          title={title}
        >
          <div className="match-content">
            <span className="match-status">{slotContent}</span>
            {homeAwayIcon && (
              <span
                className="match-location-badge"
                aria-label={isCurrentPlayerHome ? 'Home match' : 'Away match'}
              >
                {homeAwayIcon}
              </span>
            )}
          </div>
        </div>
      )
    })}

    {weekScheduledMatches.map((match) => {
      const startTime = toZonedTime(match.match_datetime, TZ)
      const weekDayIndex = weekDates.findIndex((d) => isSameDay(d, startTime))

      if (weekDayIndex === -1) return null

      const timeSlotIndex = TIME_SLOTS.findIndex(
        (slot) =>
          slot.hour === startTime.getHours() &&
          slot.minute === startTime.getMinutes(),
      )

      if (timeSlotIndex === -1) return null

      const DURATION_SLOTS = 3

      let opponentName = 'Unknown'
      const homePlayer = divisionRoster.find(
        (player) => player.id === match.home_player_id,
      )
      const awayPlayer = divisionRoster.find(
        (player) => player.id === match.away_player_id,
      )

      if (match.home_player_id === currentUserPlayerId && awayPlayer) {
        opponentName = `${awayPlayer.first_name} ${awayPlayer.last_name}`
      } else if (match.away_player_id === currentUserPlayerId && homePlayer) {
        opponentName = `${homePlayer.first_name} ${homePlayer.last_name}`
      } else if (homePlayer && awayPlayer) {
        opponentName = `${awayPlayer.first_name} ${awayPlayer.last_name}`
      }

      let opponent = null
      if (match.home_player_id === currentUserPlayerId && awayPlayer) {
        opponent = awayPlayer
      } else if (match.away_player_id === currentUserPlayerId && homePlayer) {
        opponent = homePlayer
      }

      let matchClass = 'scheduled-match-card-overlay'
      let matchIcon = '✓'
      let matchText = 'Match vs'
      let titleText = 'Scheduled match'

      const {
        isHome: isCurrentPlayerHome,
        isAway: isCurrentPlayerAway,
        icon: homeAwayIcon,
        text: homeAwayText,
      } = getHomeAwayStatus(match, currentUserPlayerId)

      if (match.status === 'normal') {
        matchClass = 'completed-match-card-overlay'
        matchIcon = '✅'
        matchText = 'Completed vs'
        titleText = 'Completed match'
      } else if (match.status === 'opponent_retired') {
        matchClass = 'completed-match-card-overlay'
        matchIcon = '🏃‍♂️'
        matchText = 'Won vs'
        titleText = 'Won by retirement'
      } else if (match.status === 'i_retired') {
        matchClass = 'completed-match-card-overlay'
        matchIcon = '🏃‍♂️'
        matchText = 'Retired vs'
        titleText = 'Lost by retirement'
      }

      if (isCurrentPlayerHome) {
        matchClass += ' home-match'
      } else if (isCurrentPlayerAway) {
        matchClass += ' away-match'
      }

      return (
        <div
          key={`match-${match.id}`}
          className={matchClass}
          style={{
            gridColumn: weekDayIndex + 2,
            gridRow: `${timeSlotIndex + 2} / span ${DURATION_SLOTS}`,
          }}
          onClick={(e) => {
            e.stopPropagation()
            handleMatchClick(match, opponent)
          }}
          title={`${titleText} with ${opponentName}${
            homeAwayText ? ` (${homeAwayText})` : ''
          } - Click for details`}
        >
          <div className="match-content">
            <span className="match-status">
              {matchIcon} {matchText} {opponentName}
            </span>
            {homeAwayIcon && (
              <span
                className="match-location-badge"
                aria-label={isCurrentPlayerHome ? 'Home match' : 'Away match'}
              >
                {homeAwayIcon}
              </span>
            )}
          </div>
        </div>
      )
    })}
  </div>
)

WeeklyCalendarOverlays.propTypes = {
  weekProposals: PropTypes.array.isRequired,
  weekScheduledMatches: PropTypes.array.isRequired,
  weekDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)).isRequired,
  TIME_SLOTS: PropTypes.arrayOf(
    PropTypes.shape({
      hour: PropTypes.number.isRequired,
      minute: PropTypes.number.isRequired,
    }),
  ).isRequired,
  unplayedOpponents: PropTypes.array.isRequired,
  allMatches: PropTypes.array.isRequired,
  currentUserPlayerId: PropTypes.number.isRequired,
  divisionRoster: PropTypes.array.isRequired,
  handleProposalClick: PropTypes.func.isRequired,
  handleMatchClick: PropTypes.func.isRequired,
}

export default WeeklyCalendarOverlays
